import { BaseModel, type RelationConfig } from './base';

import { CustomField } from './customField';
import { EmployeeService } from './employeeService';
import { ServiceAutomation } from './serviceAutomation';
import { ServiceCategory } from './serviceCategory';
import { ServiceForm } from './serviceForm';
import { ServiceResource } from './serviceResource';
import { Tax } from './tax';

import type { Employee } from './employee';
import type { Images } from '../types/interfaces/images';

export class Service extends BaseModel {
  id!: number;
  idService!: number;
  title!: string;
  description?: string;
  information?: string;
  price!: number;
  costPrice?: number;
  priceFrom?: boolean;
  duration!: number;
  processing!: number;
  finishing!: number;
  enabled!: boolean;
  deleted!: number;
  hasImage!: boolean;
  listOrder!: number;
  idServiceCategory?: number;
  employeeIDs?: number[];
  taxIDs?: number[];
  allowInternet?: boolean;
  bufferTimePre?: number;
  bufferTimePost?: number;
  onlinePaymentDepositFixed?: number | null;
  onlinePaymentRequireCardCapture?: boolean | null;
  loyaltyEnabled?: boolean;
  customerMax?: number;

  overrides!: ServiceOverrides;
  customerType!: ServiceCustomerType;
  reviewReminderMode!: ServiceReviewReminderMode;
  bufferTimeMode!: ServiceBufferTimeMode;
  customerTimeMode!: ServiceCustomerTimeMode;
  onlinePaymentDepositFixedType!: ServiceOnlinePaymentDepositFixedType;
  addOnMode!: ServiceAddOnMode;
  hasAddOns?: boolean;

  images?: Images;
  category?: ServiceCategory;
  customFields?: CustomField[];
  employees?: EmployeeService[];
  resources?: ServiceResource[];
  forms?: ServiceForm[];
  automations?: ServiceAutomation[];
  options?: ServiceOptions;

  static override relations(): RelationConfig {
    return {
      category: { type: ServiceCategory },
      customFields: { type: CustomField },
      employees: { type: EmployeeService },
      resources: { type: ServiceResource },
      forms: { type: ServiceForm },
      automations: { type: ServiceAutomation },
    };
  }

  override objectID(): string {
    return this.idService?.toString() || '';
  }

  get label(): string {
    return this.title;
  }

  get image(): string | null {
    if (this.hasImage) {
      return this.images?.square || null;
    }
    return null;
  }

  get initials(): string {
    return this.title?.charAt(0).toUpperCase() || '';
  }

  get overridesLabel(): string {
    const { hasPriceOverrides, hasZeroPricesOverrides, hasTimingOverrides } = this.overrides;

    if (hasPriceOverrides && !hasZeroPricesOverrides && hasTimingOverrides) {
      return 'service.hasAndHasTimingOverrides';
    }
    if (hasZeroPricesOverrides && hasTimingOverrides) {
      return 'service.hasZeroAndHasTimingOverrides';
    }
    if (hasTimingOverrides) {
      return 'service.hasTimingOverrides';
    }
    if (hasZeroPricesOverrides) {
      return 'service.hasZeroOverrides';
    }
    if (hasPriceOverrides && !hasZeroPricesOverrides) {
      return 'service.hasOverrides';
    }
    return '';
  }

  get formsLabel(): string {
    if (!this.forms) return '';
    const forms = this.forms.map(form => form.form?.title || '');
    return forms.join(', ');
  }

  get resourcesLabel(): string {
    if (!this.resources) return '';
    const resources = this.resources.map(resource => resource.name || '');
    return resources.join(', ');
  }

  get isServiceCustomerTypeSingle(): boolean {
    return this.customerType === ServiceCustomerType.single;
  }

  get isServiceCustomerTypeGroup(): boolean {
    return this.customerType === ServiceCustomerType.group;
  }

  get isAddOnOnly(): boolean {
    return this.addOnMode === ServiceAddOnMode.only;
  }

  get hasOverrides(): boolean {
    return !!(
      this.overrides?.hasPriceOverrides ||
      this.overrides?.hasTimingOverrides ||
      this.overrides?.hasZeroPricesOverrides
    );
  }

  get isDeleted(): boolean {
    return !!this.deleted;
  }

  get hasNoEmployees(): boolean {
    return !this.employeeIDs?.length;
  }

  get customerTypeLabel(): string {
    return ServiceCustomerTypeLabels[this.customerType] || '';
  }

  matchesQuery(query: string): boolean {
    if (this.title && this.title.toLowerCase().includes(query.toLowerCase())) {
      return true;
    }
    return false;
  }

  resourcesForAssignment(assignment: any): any[] {
    if (!this.resources) return [];

    const assignmentResources = [];
    for (const serviceResource of this.resources) {
      if (serviceResource.assignment === assignment) {
        assignmentResources.push(serviceResource);
      }
    }
    return assignmentResources;
  }

  durationForEmployee(employee: Employee): number {
    if (employee) {
      const employeeService = employee.getEmployeeServiceForService(this);
      if (employeeService && employeeService.duration) {
        return employeeService.duration;
      }
    }
    return this.duration;
  }

  processingForEmployee(employee: Employee): number {
    if (employee) {
      const employeeService = employee.getEmployeeServiceForService(this);
      if (employeeService && employeeService.processing) {
        return employeeService.processing;
      }
    }
    return this.processing;
  }

  finishingForEmployee(employee: Employee): number {
    if (employee) {
      const employeeService = employee.getEmployeeServiceForService(this);
      if (employeeService && employeeService.finishing) {
        return employeeService.finishing;
      }
    }
    return this.finishing;
  }

  priceForEmployee(employee: Employee): number {
    if (employee) {
      const employeeService = employee.getEmployeeServiceForService(this);
      if (employeeService && employeeService.price) {
        return employeeService.price;
      }
    }
    return this.price;
  }

  additionalPropertiesForReorder(): Record<string, any> {
    return {
      idServiceCategory: this.idServiceCategory,
    };
  }
}

export enum ServiceCustomerType {
  single = 0,
  group = 1,
}

export const ServiceCustomerTypeLabels: Record<ServiceCustomerType, string> = {
  [ServiceCustomerType.single]: 'general.single',
  [ServiceCustomerType.group]: 'general.group',
};

export enum ServiceReviewReminderMode {
  none = 0,
  every = 1,
  one = 2,
}

export const ServiceReviewReminderModeLabels: Record<ServiceReviewReminderMode, string> = {
  [ServiceReviewReminderMode.none]: 'general.none',
  [ServiceReviewReminderMode.every]: 'service.everyAppointment',
  [ServiceReviewReminderMode.one]: 'service.once',
};

export enum ServiceBufferTimeMode {
  aggregate = 0,
  longest = 1,
  between = 2,
}

export const ServiceBufferTimeModeLabels: Record<ServiceBufferTimeMode, string> = {
  [ServiceBufferTimeMode.aggregate]: 'service.aggregate',
  [ServiceBufferTimeMode.longest]: 'service.longest',
  [ServiceBufferTimeMode.between]: 'service.between',
};

export enum ServiceCustomerTimeMode {
  all = 0,
  duration = 1,
  durationProcessing = 2,
}

export const ServiceCustomerTimeModeLabels: Record<ServiceCustomerTimeMode, string> = {
  [ServiceCustomerTimeMode.all]: 'service.allTimeModes',
  [ServiceCustomerTimeMode.duration]: 'service.duration',
  [ServiceCustomerTimeMode.durationProcessing]: 'service.durationProcessing',
};

export enum ServiceOnlinePaymentDepositFixedType {
  fixed = 0,
  percentage = 1,
}

export const ServiceOnlinePaymentDepositFixedTypeLabels: Record<ServiceOnlinePaymentDepositFixedType, string> = {
  [ServiceOnlinePaymentDepositFixedType.fixed]: 'general.fixed',
  [ServiceOnlinePaymentDepositFixedType.percentage]: 'general.percentage',
};

export enum ServiceAddOnMode {
  notAllowed = 0,
  allowed = 1,
  only = 2,
}

export const ServiceAddOnModeLabels: Record<ServiceAddOnMode, string> = {
  [ServiceAddOnMode.notAllowed]: 'service.association.notAllowed',
  [ServiceAddOnMode.allowed]: 'service.association.allowed',
  [ServiceAddOnMode.only]: 'service.association.only',
};

export interface ServiceOverrides {
  hasPriceOverrides: boolean;
  hasTimingOverrides: boolean;
  hasZeroPricesOverrides: boolean;
  hasForms?: boolean;
  hasAutomations?: boolean;
  hasAddOns?: boolean;
}

export interface ServiceOptions {
  serviceCategories?: ServiceCategory[];
  taxes?: Tax[];
  colors?: Array<{ value: string; hex: string }>;
  customerType?: number;
}
